// src/components/SetCard.js
// Shared set card component used by SearchResults, Themes, and SubThemes.
// Renders a single LEGO set in either grid or list view with persistent
// collection/wishlist ownership badges and a unified add-to-collection flow.
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

// ---------------------------------------------------------------------------
// Ownership badge helpers
// ---------------------------------------------------------------------------

const CollectionBadge = ({ quantity }) => (
    <div
        className="absolute top-2 right-2 flex items-center justify-center gap-1 bg-green-600 text-white text-xs font-bold px-2 py-0.5 min-w-[1.5rem] min-h-[1.5rem] rounded-full shadow-md"
        title={`You own ${quantity} of this set`}
    >
        <FontAwesomeIcon icon="check" className="text-[10px]" />
        {quantity > 1 && <span>×{quantity}</span>}
    </div>
);

const WishlistBadge = ({ top }) => (
    <div
        className={`absolute ${top} right-2 flex items-center gap-1 bg-yellow-400 text-gray-900 text-xs font-bold px-2 py-0.5 rounded-full shadow-md`}
        title="In your wishlist"
    >
        <FontAwesomeIcon icon="heart" className="text-[10px]" />
        <span>Wishlist</span>
    </div>
);

// ---------------------------------------------------------------------------
// Quantity stepper (shared between grid and list)
// ---------------------------------------------------------------------------

const QuantityStepper = ({ setNum, quantity, onQuantityChange }) => (
    <div className="flex items-center gap-2 bg-gray-100 p-1.5 rounded w-full" onClick={e => e.stopPropagation()}>
        <button
            className="w-7 h-7 shrink-0 bg-red-600 text-white rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-sm font-bold"
            onClick={e => onQuantityChange(setNum, Math.max(1, quantity - 1), e)}
        >
            -
        </button>
        <input
            type="text"
            className="flex-1 min-w-0 h-7 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
            value={quantity}
            onChange={e => {
                const val = parseInt(e.target.value, 10);
                if (!isNaN(val) && val > 0) onQuantityChange(setNum, val, e);
            }}
            onClick={e => e.stopPropagation()}
        />
        <button
            className="w-7 h-7 shrink-0 bg-red-600 text-white rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-sm font-bold"
            onClick={e => onQuantityChange(setNum, quantity + 1, e)}
        >
            +
        </button>
    </div>
);

// ---------------------------------------------------------------------------
// Add buttons (shared between grid and list)
// ---------------------------------------------------------------------------

const AddButtons = ({ setNum, quantity, isInWishlist, onAddToCollection, onAddToWishlist, compact = false }) => {
    const py = compact ? 'py-1.5' : 'py-2';
    // When the set is wishlisted but not yet collected, treat the primary action as "Move to Collection"
    const collectionLabel = isInWishlist
        ? (compact ? 'Move to Collection' : 'Move to Collection')
        : (compact ? 'Collection' : 'Add To Collection');
    const collectionIcon = isInWishlist ? 'box-open' : 'plus';

    return (
        <div className={`flex ${compact ? 'flex-row flex-wrap gap-2' : 'flex-col gap-2'}`}>
            <button
                className={`${py} px-3 rounded text-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-red-500 flex items-center justify-center gap-1.5 bg-red-600 hover:bg-red-700`}
                onClick={e => onAddToCollection(setNum, quantity, e)}
            >
                <FontAwesomeIcon icon={collectionIcon} />
                <span>{collectionLabel}</span>
            </button>
            {/* Hide "Add to Wishlist" when the set is already wishlisted */}
            {!isInWishlist && (
                <button
                    className={`${py} px-3 bg-yellow-400 hover:bg-yellow-500 text-gray-900 rounded text-sm font-medium focus:outline-none focus:ring-2 focus:ring-yellow-500 flex items-center justify-center gap-1.5`}
                    onClick={e => onAddToWishlist(setNum, quantity, e)}
                >
                    <FontAwesomeIcon icon="heart" />
                    <span>{compact ? 'Wishlist' : 'Add to Wishlist'}</span>
                </button>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Main SetCard component
// ---------------------------------------------------------------------------

/**
 * Props:
 *   set              - set data object (fields: set_num, name, year, img_url, num_parts?,
 *                      minifigs?, in_collection, in_wishlist)
 *   status           - { inCollection: number, inWishlist: boolean } — merged persistent state
 *                      managed by the parent; takes precedence over set.in_collection / in_wishlist
 *   viewMode         - 'grid' (default) | 'list'
 *   showDetails      - show pieces / minifig chips (SubThemes); default false
 *   selectedQuantity - pending quantity, or undefined if card is collapsed
 *   onToggleSelect   - (setNum) => void
 *   onQuantityChange - (setNum, qty, e) => void
 *   onAddToCollection- (setNum, qty, e) => void
 *   onAddToWishlist  - (setNum, qty, e) => void
 */
const SetCard = ({
    set,
    status,
    viewMode = 'grid',
    showDetails = false,
    selectedQuantity,
    onToggleSelect,
    onQuantityChange,
    onAddToCollection,
    onAddToWishlist,
}) => {
    const [imageLoaded, setImageLoaded] = useState(false);

    const handleImageError = e => { e.target.src = '/images/lego_piece_questionmark.png'; };

    // Derive effective ownership — parent's collectionStatus takes precedence, then API fields
    const effectiveInCollection = status
        ? status.inCollection
        : Number(set.in_collection || 0);

    const effectiveInWishlist = status
        ? status.inWishlist
        : Boolean(Number(set.in_wishlist || 0));

    const isSelected = selectedQuantity !== undefined;

    // -----------------------------------------------------------------------
    // Grid view
    // -----------------------------------------------------------------------

    if (viewMode === 'list') {
        return (
            <div
                className={`bg-white rounded-lg shadow-sm border overflow-hidden transition-all duration-200 hover:shadow-md
                    ${isSelected ? 'ring-2 ring-red-500 border-red-200' : 'border-gray-200'}`}
                onClick={() => onToggleSelect(set.set_num)}
            >
                <div className="flex flex-col sm:flex-row">
                    {/* Image */}
                    <div className="relative sm:w-44 p-3 flex items-center justify-center border-b sm:border-b-0 sm:border-r border-gray-200 shrink-0">
                        {!imageLoaded && <Skeleton height={90} width={90} />}
                        <img
                            src={set.img_url}
                            alt={set.name}
                            className={`h-24 object-contain ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                            style={{ transition: 'opacity 0.3s' }}
                            onError={handleImageError}
                            onLoad={() => setImageLoaded(true)}
                        />
                        {/* Ownership badges */}
                        {effectiveInCollection > 0 && <CollectionBadge quantity={effectiveInCollection} />}
                        {effectiveInWishlist && (
                            <WishlistBadge top={effectiveInCollection > 0 ? 'top-8' : 'top-2'} />
                        )}
                    </div>

                    {/* Content */}
                    <div className="flex-grow p-4">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                            <div>
                                <h3 className="font-medium text-gray-800 line-clamp-1">{set.name}</h3>
                                <div className="flex items-center gap-3 mt-1">
                                    <span className="text-sm text-gray-600 font-mono">{set.set_num}</span>
                                    <span className="text-sm font-semibold bg-gray-100 px-2 py-0.5 rounded">{set.year}</span>
                                </div>
                                {showDetails && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {set.num_parts != null && (
                                            <span className="text-xs bg-blue-50 text-blue-700 rounded-full px-2 py-0.5 flex items-center gap-1">
                                                <FontAwesomeIcon icon="puzzle-piece" />
                                                {parseInt(set.num_parts).toLocaleString()}
                                            </span>
                                        )}
                                        {set.minifigs > 0 && (
                                            <span className="text-xs bg-yellow-50 text-yellow-700 rounded-full px-2 py-0.5 flex items-center gap-1">
                                                <FontAwesomeIcon icon="user" />
                                                {set.minifigs}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>

                    {/* Controls */}
                    {isSelected ? (
                        <div className="flex flex-wrap gap-2 items-center" onClick={e => e.stopPropagation()}>
                            <QuantityStepper
                                setNum={set.set_num}
                                quantity={selectedQuantity}
                                onQuantityChange={onQuantityChange}
                            />
                            <AddButtons
                                setNum={set.set_num}
                                quantity={selectedQuantity}
                                isInWishlist={effectiveInWishlist && effectiveInCollection === 0}
                                onAddToCollection={onAddToCollection}
                                onAddToWishlist={onAddToWishlist}
                                compact
                            />
                        </div>
                    ) : (
                        <button
                            className="py-1.5 px-3 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors flex items-center gap-1.5 self-end md:self-auto shrink-0"
                            onClick={e => { e.stopPropagation(); onToggleSelect(set.set_num); }}
                        >
                            <FontAwesomeIcon icon={effectiveInWishlist && effectiveInCollection === 0 ? 'box-open' : 'plus'} />
                            <span>
                                {effectiveInCollection > 0 ? 'Add More' : effectiveInWishlist ? 'Move to Collection' : 'Add Set'}
                            </span>
                        </button>
                    )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // -----------------------------------------------------------------------
    // Grid view (default)
    // -----------------------------------------------------------------------

    return (
        <div
            className={`bg-white rounded-xl shadow-sm border overflow-hidden transition-all duration-200 hover:shadow-md
                ${isSelected ? 'ring-2 ring-red-500 border-red-200' : 'border-gray-200'}`}
            onClick={() => onToggleSelect(set.set_num)}
        >
            <div className="p-4">
                {/* Image area with badges */}
                <div className="relative h-36 flex items-center justify-center mb-4">
                    {!imageLoaded && <Skeleton height={120} width="100%" />}
                    <img
                        src={set.img_url}
                        alt={set.name}
                        className={`h-full object-contain ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                        style={{ transition: 'opacity 0.3s' }}
                        onError={handleImageError}
                        onLoad={() => setImageLoaded(true)}
                    />
                    {/* Ownership badges */}
                    {effectiveInCollection > 0 && <CollectionBadge quantity={effectiveInCollection} />}
                    {effectiveInWishlist && (
                        <WishlistBadge top={effectiveInCollection > 0 ? 'top-9' : 'top-2'} />
                    )}
                </div>

                {/* Title */}
                <h3 className="font-medium text-gray-800 mb-2 line-clamp-2 h-12 leading-6">{set.name}</h3>

                {/* Set num + year */}
                <div className="flex justify-between items-center mb-3">
                    <span className="text-sm text-gray-600 font-mono">{set.set_num}</span>
                    <span className="text-sm font-semibold bg-gray-100 px-2 py-1 rounded">{set.year}</span>
                </div>

                {/* Optional detail chips */}
                {showDetails && (
                    <div className="flex flex-wrap gap-2 mb-4">
                        {set.num_parts != null && (
                            <span className="text-xs bg-blue-50 text-blue-700 rounded-full px-2 py-1 flex items-center gap-1">
                                <FontAwesomeIcon icon="puzzle-piece" />
                                {parseInt(set.num_parts).toLocaleString()}
                            </span>
                        )}
                        {set.minifigs > 0 && (
                            <span className="text-xs bg-yellow-50 text-yellow-700 rounded-full px-2 py-1 flex items-center gap-1">
                                <FontAwesomeIcon icon="user" />
                                {set.minifigs}
                            </span>
                        )}
                    </div>
                )}

                {/* Add controls */}
                {isSelected ? (
                    <div className="space-y-2" onClick={e => e.stopPropagation()}>
                        <QuantityStepper
                            setNum={set.set_num}
                            quantity={selectedQuantity}
                            onQuantityChange={onQuantityChange}
                        />
                        <AddButtons
                            setNum={set.set_num}
                            quantity={selectedQuantity}
                            isInWishlist={effectiveInWishlist && effectiveInCollection === 0}
                            onAddToCollection={onAddToCollection}
                            onAddToWishlist={onAddToWishlist}
                        />
                    </div>
                ) : (
                    <button
                        className="w-full py-2 px-3 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors flex items-center justify-center gap-2"
                        onClick={e => { e.stopPropagation(); onToggleSelect(set.set_num); }}
                    >
                        <FontAwesomeIcon icon={effectiveInWishlist && effectiveInCollection === 0 ? 'box-open' : 'plus'} />
                        <span>
                            {effectiveInCollection > 0 ? 'Add More' : effectiveInWishlist ? 'Move to Collection' : 'Add Set'}
                        </span>
                    </button>
                )}
            </div>
        </div>
    );
};

export default SetCard;
